import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { setupAxiosInterceptors } from "./services/authService";

// ===== Public pages =====
import Login from "./pages/Login";
import Register from "./pages/Register";

import Profile from "./pages/Profile";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Explore from "./pages/Explore";
import Favourite from "./pages/Favourite";

import HistoryAppointment from "./pages/HistoryAppointment";
import SPHomeLanding from "./pages/SPHomeLanding";
import PageSeo from "./components/PageSeo";
import GuidePage from "./pages/GuidePage";
import seoContent from "./seo/content.mjs";

// ===== Components =====
import PrivateRoute from "./components/PrivateRoute";
import PageLoader from "./components/PageLoader";

// Load management screens only when their routes are visited.
const RoomList = React.lazy(() => import("./pages/RoomList"));
const DetailRoom = React.lazy(() => import("./pages/DetailRoom"));
const Revenue = React.lazy(() => import("./pages/Revenue"));
const Dashboard = React.lazy(() => import("./pages/admin/Dashboard"));
const StaffManagement = React.lazy(() => import("./pages/admin/StaffManagement"));
const ScheduleManagement = React.lazy(() => import("./pages/admin/ScheduleManagement"));
const RegisterManagement = React.lazy(() => import("./pages/admin/RegisterManagement"));
const RoomManagement = React.lazy(() => import("./pages/admin/RoomManagement"));
const DetailRoomManagement = React.lazy(() => import("./pages/admin/DetailRoomManagement"));
const AssignmentSchedule = React.lazy(() => import("./pages/admin/AssignmentSchedule"));
const AdministratorProfile = React.lazy(() => import("./pages/admin/AdministratorProfile"));
const CreateRoom = React.lazy(() => import("./pages/admin/CreateRoom"));
const UpdateRoom = React.lazy(() => import("./pages/admin/UpdateRoom"));
const BuildingManagement = React.lazy(() => import("./pages/admin/BuildingManagement"));
const RevenueManagement = React.lazy(() => import("./pages/admin/RevenueManagement"));
const InvoiceManagement = React.lazy(() => import("./pages/admin/InvoiceManagement"));
const TenantsManagement = React.lazy(() => import("./pages/admin/TenantsManagement"));
const CreateInvoiceManagement = React.lazy(() => import("./pages/admin/CreateInvoiceManagement"));
const CreateBuilding = React.lazy(() => import("./pages/admin/CreateBuilding"));
const UpdateBuilding = React.lazy(() => import("./pages/admin/UpdateBuilding"));
const CreateContract = React.lazy(() => import("./pages/admin/CreateContract"));
const ContractManagement = React.lazy(() => import("./pages/admin/ContractManagement"));
const CreateTenants = React.lazy(() => import("./pages/admin/CreateTenants"));
const RevenueCustomer = React.lazy(() => import("./pages/admin/RevenueCustomer"));
const AdminAnalytics = React.lazy(() => import("./pages/admin/AnalyticsGrowth"));
const SidebarAdmin = React.lazy(() => import("./components/SidebarAdmin"));
const RevenueBoss = React.lazy(() => import("./pages/admin/RevenueBoss"));
const BossExpense = React.lazy(() => import("./pages/admin/BossExpense"));
const BossDebtTenant = React.lazy(() => import("./pages/admin/BossDebtTenant"));
const BossDebtLandlord = React.lazy(() => import("./pages/admin/BossDebtLandlord"));
const BossProfit = React.lazy(() => import("./pages/admin/BossProfit"));
const CTVTransaction = React.lazy(() => import("./pages/admin/CTVTransaction"));

const SuperAdminRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return user?.role === "super_admin"
    ? children
    : <Navigate to="/" replace />;
};

function App() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = user?.role;
  const adminRoles = ["super_admin", "boss", "admin", "assistant"];

  // ✅ Setup axios interceptor
  React.useEffect(() => {
    setupAxiosInterceptors();
  }, []);

  return (
    <Router>
      <PageSeo />
      <React.Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ===== Trang chủ ===== */}
        <Route
          path="/"
          element={
            role && adminRoles.includes(role) ? (
              role === "super_admin" ? (
                <Navigate to="/super-admin/analytics/growth" replace />
              ) : (
                <Navigate to="/admin/dashboard" replace />
              )
            ) : (
              <SPHomeLanding />
            )
          }
        />

        {/* ===== Auth ===== */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* ===== Public ===== */}
        <Route path="/about" element={<About />} />
        <Route path="/rooms" element={<RoomList />} />
        <Route path="/contact" element={<Contact />} />
        {seoContent.guides.map((guide) => (
          <Route key={guide.path} path={guide.path} element={<GuidePage guide={guide} />} />
        ))}
        <Route path="/explore" element={<Explore />} />
        <Route path="/favourite" element={<Favourite />} />
        <Route path="/room/:id" element={<DetailRoom />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/revenue" element={<Revenue />} />
        <Route path="/history" element={<HistoryAppointment />} />
        <Route
          path="/super-admin/analytics/growth"
          element={
            <SuperAdminRoute>
              <AdminAnalytics />
            </SuperAdminRoute>
          }
        />

        {/* ===== Admin routes ===== */}
        <Route path="/admin" element={<SidebarAdmin />}>
          {/* Dashboard */}
          <Route path="dashboard" element={<Dashboard />} />

          {/* Quản lý phòng */}
          <Route path="room" element={<RoomManagement />} />
          <Route path="room/create" element={<CreateRoom />} />
          <Route path="room/update/:id" element={<UpdateRoom />} />
          <Route path="room/:id" element={<DetailRoomManagement />} />

          {/* Quản lý tòa nhà */}
          <Route path="building" element={<BuildingManagement />} />
          <Route path="building/create" element={<CreateBuilding />} />
          <Route path="building/update/:id" element={<UpdateBuilding />} />

          {/* Quản lý lịch */}
          <Route path="schedules" element={<ScheduleManagement />} />
          <Route path="my-schedules" element={<AssignmentSchedule />} />

          {/* Quản lý nhân sự / hồ sơ / đăng ký */}
          <Route path="staff" element={<StaffManagement />} />
          <Route path="registermanage" element={<RegisterManagement />} />
          <Route path="administratorprofile" element={<AdministratorProfile />} />

          {/* Quản lý doanh thu / hóa đơn / người thuê */}
          <Route path="invoice/create" element={<CreateInvoiceManagement />} />
          <Route path="revenue" element={<RevenueManagement />} />
          <Route path="revenueCustomer" element={<RevenueCustomer />} />
          <Route path="dashboard/revenue" element={<RevenueBoss />} />
          <Route path="dashboard/expense" element={<BossExpense />} />
          <Route path="dashboard/debt-tenant" element={<BossDebtTenant />} />
          <Route path="dashboard/debt-landlord" element={<BossDebtLandlord />} />
          <Route path="dashboard/profit" element={<BossProfit />} />
          <Route path="invoice" element={<InvoiceManagement />} />
          <Route path="tenants" element={<TenantsManagement />} />
          <Route path="ctv/:id" element={<CTVTransaction />} />

          {/* Quản lý hợp đồng */}
          <Route path="contracts" element={<ContractManagement />} />
          <Route path="create-contract/:roomId" element={<CreateContract />} />

          {/* 🆕 Thêm route Tạo người thuê */}
          <Route path="create-tenant/:id" element={<CreateTenants />} />

          {/* Redirect mặc định */}
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* ===== 404 ===== */}
        <Route
          path="*"
          element={<h2 style={{ padding: 24 }}>404 - Trang không tồn tại</h2>}
        />
      </Routes>
      </React.Suspense>
    </Router>
  );
}

export default App;
